
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { VerificationStatus, PaymentStatus, CardDetails } from '@/types/payment';
import { useSafeWebhook } from '@/hooks/useSafeWebhook';

export const usePaymentProcessing = (addTransaction: Function) => {
  const [aiVerificationStatus, setAiVerificationStatus] = useState<VerificationStatus>('idle');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('pending');
  const [aiVerificationScore, setAiVerificationScore] = useState<number | null>(null);
  const [aiRiskLevel, setAiRiskLevel] = useState<'low' | 'medium' | 'high' | null>(null);
  const [stripeReady, setStripeReady] = useState<boolean>(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [cardBrand, setCardBrand] = useState<string | null>(null);
  const [cardLast4, setCardLast4] = useState<string | null>(null);
  const [cardDetails, setCardDetails] = useState<CardDetails | null>(null);
  
  const [aiStripeVerificationStatus, setAiStripeVerificationStatus] = useState<VerificationStatus>('idle');
  const [aiStripeVerificationScore, setAiStripeVerificationScore] = useState<number | null>(null);
  const [aiStripeRiskLevel, setAiStripeRiskLevel] = useState<'low' | 'medium' | 'high' | null>(null);
  const [aiStripeCardBrand, setAiStripeCardBrand] = useState<string | null>(null);
  const [aiStripeCardLast4, setAiStripeCardLast4] = useState<string | null>(null);
  const [aiStripeError, setAiStripeError] = useState<string | null>(null);
  const [aiStripeReady, setAiStripeReady] = useState<boolean>(false);
  
  const { toast } = useToast();
  const webhookContext = useSafeWebhook();
  
  // Generate a unique ID without relying on external packages
  const generateId = () => {
    return 'id_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
  };

  const processPayment = async (amount: number, recipientId: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const transactionId = generateId();
        addTransaction({
          amount,
          fee: amount * 0.05,
          recipient: recipientId,
          sender: 'Unknown', // This should be replaced with actual user data
          status: 'completed',
          method: 'bluetooth',
          recipientEmail: `${recipientId}@example.com`,
          senderEmail: 'unknown@example.com',
        });
        resolve(true);
      }, 2000);
    });
  };

  const processAiPayment = async (
    amount: number,
    recipientId: string,
    cardDetails: CardDetails,
    verificationScore?: number
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      setAiVerificationStatus('verifying');
      setPaymentStatus('processing');

      setTimeout(async () => {
        const isVerified = Math.random() > 0.5;
        const riskLevel = isVerified ? 'low' : 'high';
        setAiVerificationScore(verificationScore || (isVerified ? 85 : 30));
        setAiRiskLevel(riskLevel);

        if (isVerified) {
          setAiVerificationStatus('verified');
          setPaymentStatus('succeeded');

          const transactionId = generateId();
          addTransaction({
            amount,
            fee: amount * 0.05,
            recipient: recipientId,
            sender: 'Unknown', // This should be replaced with actual user data
            status: 'completed',
            method: 'card',
            recipientEmail: `${recipientId}@example.com`,
            senderEmail: 'unknown@example.com',
            metadata: {
              ai_verified: true,
              ai_score: verificationScore || 85,
              risk_level: riskLevel,
              cardBrand: 'Visa',
              last4: cardDetails.cardNumber.slice(-4),
            },
          });

          toast({
            title: "Payment Successful",
            description: "Payment processed successfully",
          });
          
          if (webhookContext && typeof webhookContext.triggerWebhook === 'function') {
            webhookContext.triggerWebhook({
              event: 'payment_success', 
              data: { 
                amount, 
                recipientId, 
                transactionId 
              }
            });
          }
          
          resolve(true);
        } else {
          setAiVerificationStatus('failed');
          setPaymentStatus('failed');
          toast({
            title: "Payment Failed",
            description: "Card verification failed",
            variant: 'destructive',
          });
          
          if (webhookContext && typeof webhookContext.triggerWebhook === 'function') {
            webhookContext.triggerWebhook({
              event: 'payment_failed', 
              data: { 
                amount, 
                recipientId 
              }
            });
          }
          
          reject(new Error("Card verification failed"));
        }
      }, 3000);
    });
  };

  // Enhanced Stripe payment
  const processEnhancedAiStripePayment = async (
    cardData: any, 
    amount: number, 
    recipientId: string, 
    description?: string
  ) => {
    console.log(`Processing enhanced AI Stripe payment: $${amount} to ${recipientId}`);
    // Mock implementation
    const transactionId = `tx_${Date.now()}`;
    
    // Add transaction
    addTransaction({
      amount,
      fee: amount * 0.05,
      recipient: recipientId,
      sender: 'Current User',
      status: 'completed',
      method: 'stripe',
      recipientEmail: `${recipientId}@example.com`,
      senderEmail: 'current.user@example.com',
      // Include description as part of the metadata
      metadata: description ? { description } : undefined
    });
    
    return {
      success: true,
      transactionId,
      verificationScore: 90
    };
  };
  
  const processAiCardPayment = async (cardData: any, amount: number, recipientId: string) => {
    // Mock implementation
    return true;
  };
  
  const processAiStripePayment = async (cardData: any, amount: number, recipientId: string) => {
    // Mock implementation
    return true;
  };
  
  const aiCardVerify = async (cardData: any) => {
    // Mock implementation
    return {
      success: true,
      score: 85,
      riskLevel: 'low' as 'low' | 'medium' | 'high',
      cardBrand: 'Visa',
      last4: '4242'
    };
  };

  return {
    processPayment,
    processAiPayment,
    processEnhancedAiStripePayment,
    processAiCardPayment,
    processAiStripePayment,
    aiCardVerify,
    
    // Verification status states
    aiVerificationStatus,
    setAiVerificationStatus,
    paymentStatus,
    setPaymentStatus,
    aiVerificationScore,
    setAiVerificationScore,
    aiRiskLevel,
    setAiRiskLevel,
    
    // Stripe states
    stripeReady,
    setStripeReady,
    stripeError,
    setStripeError,
    cardBrand,
    setCardBrand,
    cardLast4,
    setCardLast4,
    cardDetails,
    setCardDetails,
    
    // AI Stripe states
    aiStripeVerificationStatus,
    setAiStripeVerificationStatus,
    aiStripeVerificationScore,
    setAiStripeVerificationScore,
    aiStripeRiskLevel,
    setAiStripeRiskLevel,
    aiStripeCardBrand,
    setAiStripeCardBrand,
    aiStripeCardLast4,
    setAiStripeCardLast4,
    aiStripeError,
    setAiStripeError,
    aiStripeReady,
    setAiStripeReady,
  };
};
